export const UNLIMITED_LABEL = "Unlimited";
export const UNLIMITED_SORT = Number.POSITIVE_INFINITY;

export const expirySortValue = (d?: Date | null): number =>
  d ? d.getTime() : UNLIMITED_SORT;

// -------- UTC getters --------
export const getYear = (d: Date): number => d.getUTCFullYear();
export const getMonth = (d: Date): number => d.getUTCMonth(); // 0-11
export const getDate = (d: Date): number => d.getUTCDate(); // 1-31
export const getDay = (d: Date): number => d.getUTCDay(); // 0-6
export const getHours = (d: Date): number => d.getUTCHours();
export const getMinutes = (d: Date): number => d.getUTCMinutes();
export const getSeconds = (d: Date): number => d.getUTCSeconds();

// -------- UTC setters --------
export const setYear = (d: Date, year: number): void => {
  d.setUTCFullYear(year);
};
export const setMonth = (d: Date, month: number): void => {
  d.setUTCMonth(month);
};
export const setDate = (d: Date, date: number): void => {
  d.setUTCDate(date);
};
export const setHours = (
  d: Date,
  hours: number,
  minutes?: number,
  seconds?: number,
  ms?: number,
): void => {
  d.setUTCHours(hours, minutes ?? 0, seconds ?? 0, ms ?? 0);
};

// Fast pad2 without padStart allocations
const pad2 = (n: number) => (n < 10 ? `0${n}` : `${n}`);

/** Formats as YYYY-MM-DD or "Unlimited". Uses UTC to avoid timezone shifts. */
export const formatDateYMD = (d?: Date | null): string => {
  if (!d) return UNLIMITED_LABEL;
  const y = d.getUTCFullYear();
  const m = pad2(d.getUTCMonth() + 1);
  const day = pad2(d.getUTCDate());

  return `${y}-${m}-${day}`;
};

/** Now (Date is epoch-based; UTC/local is only about getters) */
export const now = (): Date => new Date();

/** Start of day UTC */
export const startOfDay = (d: Date): Date => {
  const x = new Date(d.getTime());
  x.setUTCHours(0, 0, 0, 0);

  return x;
};

/** End of day UTC */
export const endOfDay = (d: Date): Date => {
  const x = new Date(d.getTime());
  x.setUTCHours(23, 59, 59, 999);

  return x;
};

const daysInMonthUTC = (year: number, month0: number): number => {
  // month0 is 0-11; day 0 of next month is last day of current month
  return new Date(Date.UTC(year, month0 + 1, 0)).getUTCDate();
};

/**
 * Adds months in UTC, clamping the day-of-month to end of target month.
 * Example: 2025-01-31 + 1 => 2025-02-28 (or 29 in leap years)
 */
export const addMonths = (d: Date, months: number): Date => {
  const year = d.getUTCFullYear();
  const month0 = d.getUTCMonth();
  const day = d.getUTCDate();

  const targetMonth0 = month0 + months;

  // Start at day 1 to avoid overflow while setting month/year
  const x = new Date(Date.UTC(year, targetMonth0, 1));
  const dim = daysInMonthUTC(x.getUTCFullYear(), x.getUTCMonth());

  x.setUTCDate(Math.min(day, dim));
  // Preserve time
  x.setUTCHours(
    d.getUTCHours(),
    d.getUTCMinutes(),
    d.getUTCSeconds(),
    d.getUTCMilliseconds(),
  );

  return x;
};

/**
 * Get the start of the week (Monday) for a given date in UTC.
 * ISO 8601 standard: weeks start on Monday.
 */
export const getStartOfWeek = (d: Date): Date => {
  const x = new Date(d);
  const day = x.getUTCDay();
  const diff = (day + 6) % 7;

  x.setUTCDate(x.getUTCDate() - diff);
  x.setUTCHours(0, 0, 0, 0);

  return x;
};

/**
 * Get the end of the week (Sunday) for a given date in UTC.
 * ISO 8601 standard: weeks end on Sunday.
 */
export const getEndOfWeek = (d: Date): Date => {
  const x = new Date(d);
  const day = x.getUTCDay();
  const diff = (7 - day) % 7;

  x.setUTCDate(x.getUTCDate() + diff);

  return x;
};

/**
 * Check if two dates are in the same week (ISO 8601).
 */
export const isSameWeek = (date1: Date, date2: Date): boolean => {
  const start1 = getStartOfWeek(date1).getTime();
  const start2 = getStartOfWeek(date2).getTime();

  return start1 === start2;
};

/**
 * Check if a date is in the past.
 */
export const hasDatePassed = (d: Date): boolean => {
  return Date.now() > d.getTime();
};
